import vtVisitorId from '@nrdy-marketing-engine/tracking/src/visitor-ids';
import { omitBy } from '@s-libs/micro-dash';
import Cookies from 'js-cookie';
import SubmitFormAction from '../submit-form-action';
import submitForm from './submit-form';
const isEmpty = (v) => v === undefined || v === null || v === '';

const KNOWN_FIELDS = [
  'abtest',
  'ab_test_name',
  'ab_test_variant',
  'callback_time',
  'correlation_id',
  'country_code',
  'dialer_consent_accepted',
  'email',
  'full_name',
  'leadform',
  'password',
  'phone_number',
  'referred_by_url',
  'spells',
  'subjects',
  'tutoring-type',
  'vtcampaign',
  'vtmedium',
  'vtplacement',
  'vtsource',
  'zip_code',
];

/**
 * Returns a string of all additional form fields concatenated for the `message` field.
 */
function getMessageValue(formData: Record<string, any>) {
  return Object.entries(formData)
    .filter(([key, value]) => value && !KNOWN_FIELDS.includes(key))
    .map(([key, value]) => `${key}: ${value}`)
    .join(' | ');
}

/**
 * BI requires the answers as a hash at root level as there is currently no support
 * for nested hashes in the 'anything goes' params
 */
function getAdditionalFormData(formData: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(formData)
      .filter(([key, value]) => value && !KNOWN_FIELDS.includes(key))
      .map(([key, value]) => [`lead_form.${key.replace('-', '_')}`, value])
  );
}

/**
 * Creates the leads Page field from the conversion page url and params we want
 * to pass to vt-contact (including query string on conversion page, as well as
 * attribution fields from hidden input fields).
 *
 * @param {element} form
 * @returns {string}
 */
function getPageValue(): string {
  const page = new URL(window.location.href);
  page.hash = '';

  // remove query params that are not of our concern
  for (const key of page.searchParams.keys()) {
    if (!KNOWN_FIELDS.includes(key)) {
      page.searchParams.delete(key);
    }
  }

  return page.toString();
}

/**
 * Builds the JSON payload body for the create_contact form submission.
 */
function getPayload(formData: Record<string, any>) {
  return {
    data: omitBy(
      {
        name: {
          full_name: formData.full_name,
        },
        contact_details: omitBy(
          {
            email: formData.email,
            phone: formData.phone_number,
            dialer_consent_accepted: formData.agree_to_terms || formData.dialer_consent_accepted,
          },
          isEmpty
        ),
        location: {
          country_code: formData.country_code || 'US',
          zip_code: formData.zip_code,
        },
        tutoring_preferences: {
          types: formData['tutoring-type'] || formData['tutoring_type'],
          subjects: formData.subjects || Cookies.get('subject'),
        },
        page: getPageValue(),
        visitor_id: vtVisitorId(),
        referer: Cookies.get('referer') || document.referrer,
        landing_page: Cookies.get('landing_page') || location.href,
        message: getMessageValue(formData),
        callback_time: formData.time_to_call || formData.callback_time,
        primary_language: formData.primary_language,
        ab_test_name: formData.ab_test_name,
        ab_test_variant: formData.ab_test_variant,
        ...getAdditionalFormData(formData),
      },
      isEmpty
    ),
  };
}

/**
 * Posts a new contact request to the vt-contacts API.
 */
export default async function newContact(action: SubmitFormAction) {
  const url = new URL('/v1/contact/requests', process.env.NEXT_PUBLIC_VT_API_ENDPOINT);
  return await submitForm(url, getPayload(action.formData), 'POST', {
    mode: 'cors',
    credentials: 'include',
    redirect: 'follow',
  });
}
